import { useInfiniteQuery } from "@tanstack/react-query";

import type { SearchCandidateResult } from "@/components/companies/jobs/search-v2/searchV2CandidateResult";

import { useCurrentJobId } from "@/hooks/useCurrentJobId";
import { getSearchPageMetrics } from "@/hooks/queries/useGetSearchV2Candidates";
import {
  SEARCH_V2_ANALYTICS_METADATA,
  SEARCH_V2_PAGE_SIZE,
} from "@/lib/constants";
import { mixpanelUtils } from "@/lib/mixpanel/mixpanel";
import { MIXPANEL_SCREENS } from "@/lib/mixpanel/types";
import { novaClient } from "@/lib/trpc";

/**
 * Search restricted to an explicit candidate set, paged like real search.
 *
 * Two paging modes, because keywords rank the set here rather than narrow it:
 * with keywords nova must see every id per page or the best match gets buried,
 * so send them all and walk nova's page token. Without keywords there's no
 * ranking signal, so page the id list itself and keep the caller's APR order —
 * nova's token can't drive this mode, since each request looks like a complete
 * single-page corpus to it.
 *
 * Calls `byIds` as a mutation so the ids ride in the POST body.
 */
export function useGetCandidatesByIds({
  enabled,
  keywords,
  userIds,
}: {
  enabled: boolean;
  keywords: string[];
  userIds: number[];
}) {
  const jobId = useCurrentJobId();
  const novaUtils = novaClient.useUtils();
  const isRanked = keywords.length > 0;

  return useInfiniteQuery({
    enabled: enabled && userIds.length > 0,
    // Paging mode changes what a page param means, so it's part of the key.
    queryKey: ["candidates-by-ids", jobId, userIds, keywords, isRanked],
    queryFn: async ({ pageParam }) => {
      const page = await novaUtils.client.internal.candidacy.byIds.mutate({
        jobId,
        userIds: isRanked
          ? userIds
          : userIds.slice(pageParam, pageParam + SEARCH_V2_PAGE_SIZE),
        filters: {
          keywords: isRanked ? keywords.join(", ") : undefined,
        },
        limit: SEARCH_V2_PAGE_SIZE,
        offset: isRanked ? pageParam : 0,
      });

      // Sally-mode searches feed the same dashboards as regular search.
      // `search_mode` keeps them separable so APR-ordered pages don't skew
      // search-quality metrics.
      const metrics = getSearchPageMetrics(page);
      if (metrics) {
        mixpanelUtils.trackSearchResultsLoaded(MIXPANEL_SCREENS.JOB, {
          ...metrics,
          ...SEARCH_V2_ANALYTICS_METADATA,
          corpus_size: userIds.length,
          job_id: jobId,
          keyword_count: keywords.length,
          page_token: String(pageParam),
          search_mode: "sally",
        });
      }

      return page as unknown as {
        results: SearchCandidateResult[];
        nextPageToken?: string;
        totalCount: number;
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      const loaded = allPages.length * SEARCH_V2_PAGE_SIZE;
      // Ranked mode follows nova's token. Sliced mode knows the corpus
      // locally (nova only ever sees one page's worth of ids at a time).
      if (isRanked) {
        return lastPage.nextPageToken === undefined
          ? undefined
          : Number(lastPage.nextPageToken);
      }
      return loaded < userIds.length ? loaded : undefined;
    },
  });
}
